import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, MessageCircle, X } from "lucide-react";
import { WA_NUMBER, IG_DM, buildGeneralMessage } from "../../lib/order";

// WhatsApp glyph (simple outlined mark that plays well with our monochrome theme)
function WhatsAppMark({ className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.198.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.372-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.371-.272.298-1.04 1.017-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.695.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347zM12 2C6.477 2 2 6.477 2 12c0 1.752.457 3.457 1.324 4.964L2 22l5.164-1.303A9.958 9.958 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18.31a8.28 8.28 0 0 1-4.2-1.15l-.3-.18-3.06.772.82-2.98-.196-.31A8.28 8.28 0 1 1 20.31 12c0 4.588-3.723 8.31-8.31 8.31z" />
    </svg>
  );
}

export default function QuickChat() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const waHref = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
    buildGeneralMessage()
  )}`;

  return (
    <div
      ref={ref}
      className="fixed bottom-5 right-5 z-[80] md:bottom-8 md:right-8 print:hidden"
      data-testid="quick-chat"
    >
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.76, 0, 0.24, 1] }}
            className="mb-3 w-[260px] overflow-hidden rounded-2xl border border-black/10 bg-white/95 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)] backdrop-blur-xl"
          >
            <div className="border-b border-black/5 px-5 pt-4 pb-3">
              <p className="font-body text-[10px] uppercase tracking-[0.3em] text-black/40">
                Chat with us
              </p>
              <p className="mt-1 font-serif-editorial text-lg italic text-black/80">
                We&apos;d love to help you order.
              </p>
            </div>
            <a
              data-testid="quick-chat-whatsapp"
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 border-b border-black/5 px-5 py-3.5 transition-colors hover:bg-[#F5F1E8]"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366] text-white">
                <WhatsAppMark className="h-4 w-4" />
              </span>
              <span className="flex-1">
                <span className="block font-body text-[11px] uppercase tracking-[0.22em] text-black/50">
                  WhatsApp us
                </span>
                <span className="block font-body text-[13px] text-black">
                  +91 81379 21196
                </span>
                <span className="mt-0.5 flex items-center gap-1 font-body text-[10px] italic text-[#25D366]/90">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inset-0 animate-ping rounded-full bg-[#25D366] opacity-70" />
                    <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-[#25D366]" />
                  </span>
                  usually replies in ~10 min
                </span>
              </span>
            </a>
            <a
              data-testid="quick-chat-instagram"
              href={IG_DM}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-[#F5F1E8]"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white">
                <Instagram className="h-4 w-4" strokeWidth={1.6} />
              </span>
              <span className="flex-1">
                <span className="block font-body text-[11px] uppercase tracking-[0.22em] text-black/50">
                  Instagram DM
                </span>
                <span className="block font-body text-[13px] text-black">
                  @keilani_clothing
                </span>
              </span>
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="quick-chat-toggle"
        aria-label={open ? "Close chat menu" : "Open chat menu"}
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.4, duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#050505] text-white shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)] ring-1 ring-white/10 transition-colors hover:bg-[var(--kln-gold)] hover:text-black"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span
              key="x"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.22 }}
            >
              <X className="h-5 w-5" strokeWidth={1.6} />
            </motion.span>
          ) : (
            <motion.span
              key="chat"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.22 }}
            >
              <MessageCircle className="h-5 w-5" strokeWidth={1.6} />
            </motion.span>
          )}
        </AnimatePresence>
        {!open && (
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#25D366] ring-2 ring-[#050505]" />
        )}
      </motion.button>
    </div>
  );
}

export { WhatsAppMark };

export function ReplyNudge({ testid = "wa-reply-nudge", className = "" }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 font-serif-editorial text-[13px] italic text-[#25D366] ${className}`}
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inset-0 animate-ping rounded-full bg-[#25D366] opacity-70" />
        <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-[#25D366]" />
      </span>
      usually replies in ~10 min
    </span>
  );
}
